import { normalizeClientCode } from '@/composables/client-code-styles';
import { useOSettings } from '@/composables/use-settings';
import { computed } from 'vue';

type CardModule = Record<string, unknown> | (() => Promise<Record<string, unknown>>);

function getTypeNameFromPath(path: string): string {
  return path.split('/').pop()?.replace('.vue', '') ?? '';
}

function buildGenericTypeMaps(modules: Record<string, unknown>) {
  const maps: Record<string, CardModule> = Object.create(null);

  for (const [modulePath, moduleValue] of Object.entries(modules)) {
    const fileName = getTypeNameFromPath(modulePath);

    if (fileName) {
      maps[fileName] = moduleValue as CardModule;
    }
  }

  return maps;
}

function buildCustomerTypeMaps(modules: Record<string, unknown>) {
  const maps: Record<string, Record<string, CardModule>> = Object.create(null);

  for (const [modulePath, moduleValue] of Object.entries(modules)) {
    const clientCode = modulePath.match(/\/item-cards\/([^/]+)\/[^/]+\.vue$/)?.[1];
    const fileName = getTypeNameFromPath(modulePath);

    if (!clientCode || !fileName) {
      continue;
    }

    if (!Object.hasOwn(maps, clientCode)) {
      maps[clientCode] = Object.create(null);
    }

    maps[clientCode][fileName] = moduleValue as CardModule;
  }

  return maps;
}

let genericCardTypeMaps: Record<string, CardModule> = Object.create(null);
let itemCardTypeMapsByClientCodeState: Record<string, Record<string, CardModule>> = Object.create(null);

/** Generic item-card map (no customer override). Kept for backward compatibility. */
export const itemCardTypeMaps: Record<string, CardModule> = new Proxy({} as Record<string, CardModule>, {
  get(_target, prop: string) {
    return genericCardTypeMaps[prop];
  },
  ownKeys() {
    return Reflect.ownKeys(genericCardTypeMaps);
  },
  getOwnPropertyDescriptor(_target, prop) {
    if (typeof prop === 'string' && Object.hasOwn(genericCardTypeMaps, prop)) {
      return {
        configurable: true,
        enumerable: true,
        value: genericCardTypeMaps[prop],
      };
    }

    return undefined;
  },
});

export const itemCardTypeMapsByClientCode: Record<string, Record<string, CardModule>> = new Proxy(
  {} as Record<string, Record<string, CardModule>>,
  {
    get(_target, prop: string) {
      return itemCardTypeMapsByClientCodeState[prop];
    },
    ownKeys() {
      return Reflect.ownKeys(itemCardTypeMapsByClientCodeState);
    },
    getOwnPropertyDescriptor(_target, prop) {
      if (prop in itemCardTypeMapsByClientCodeState) {
        return {
          configurable: true,
          enumerable: true,
          value: itemCardTypeMapsByClientCodeState[prop as string],
        };
      }

      return undefined;
    },
  },
);

/** Supply glob-discovered item-card modules from the host app (Vite root). */
export function configureItemCardModules(genericModules: Record<string, unknown>, customerModules: Record<string, unknown>) {
  genericCardTypeMaps = buildGenericTypeMaps(genericModules);
  itemCardTypeMapsByClientCodeState = buildCustomerTypeMaps(customerModules);
}

export function resetItemCardModulesForTests() {
  genericCardTypeMaps = Object.create(null);
  itemCardTypeMapsByClientCodeState = Object.create(null);
}

export function resolveItemCardModule(type: string, clientCode?: string | null) {
  const normalized = normalizeClientCode(clientCode);

  if (
    normalized &&
    Object.hasOwn(itemCardTypeMapsByClientCodeState, normalized) &&
    Object.hasOwn(itemCardTypeMapsByClientCodeState[normalized], type)
  ) {
    return itemCardTypeMapsByClientCodeState[normalized][type];
  }

  return Object.hasOwn(genericCardTypeMaps, type) ? genericCardTypeMaps[type] : undefined;
}

export function useHCardResolver() {
  const { app_client_code } = useOSettings();

  function resolveItemCard(type: string) {
    return resolveItemCardModule(type, app_client_code?.value);
  }

  const resolvedItemCardTypeMaps = computed(() => {
    const normalized = normalizeClientCode(app_client_code?.value);
    const customerMaps =
      normalized && Object.hasOwn(itemCardTypeMapsByClientCodeState, normalized)
        ? itemCardTypeMapsByClientCodeState[normalized]
        : Object.create(null);

    return { ...genericCardTypeMaps, ...customerMaps };
  });

  return {
    itemCardTypeMaps: genericCardTypeMaps,
    itemCardTypeMapsByClientCode: itemCardTypeMapsByClientCodeState,
    resolveItemCard,
    resolvedItemCardTypeMaps,
  };
}
